import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppInput from '@nxsys/components/AppInput';
import AppRadioGroup from '@nxsys/components/AppRadioGroup';
import AppWorkingPatternSchedule from '@nxsys/components/AppWorkingPatternSchedule';
import { SCHEDULE_DAYS } from '@nxsys/components/AppWorkingPatternSchedule/days';
import { yesNoOptions } from '@nxsys/constants/AppConstants';
import { WORKING_PATTERN_OPTIONS } from './workingPatternOptions';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { Option } from '@nxsys/types/models/Common';
import { Col, Form, Row } from 'antd';
import { FormProps } from 'antd/lib';
import React, { Ref, useImperativeHandle } from 'react';
import AppRateInput from '@nxsys/components/AppRateInput';

type WorkingPatternFormProps = {
  formRef?: Ref<WorkingPatternFormRef>;
  onSubmit?: (values: WorkingPatternValues) => Promise<void>;
  defaultValues?: Partial<WorkingPatternValues>;
};

export type WorkingPatternFormRef = {
  submit: () => void;
  reset: () => void;
};

export type WorkingPatternValues = {
  name: string;
  isDefault: boolean;
  workingDaysFTE: string;
  workingDays: string;
  workingHoursFTE: string;
  workingHours: string;
  workingPattern: Option<string>[] | null;
};

// NX-4948: same bounds as the Old UI schema and the BE DTO (@Min/@Max) —
// days 0–7, hours 0–168.
const rangeRule = (label: string, max: number) => ({
  validator: async (_: unknown, value?: string | null) => {
    if (value === undefined || value === null || value === '') return;
    const n = Number(value);
    if (Number.isNaN(n)) throw new Error(`${label} must be a number`);
    if (n < 0) throw new Error(`${label} must be greater than or equal to 0`);
    if (n > max) {
      throw new Error(`${label} must be less than or equal to ${max}`);
    }
  },
});

// NX-4948: Mon–Sun checkbox table, as in the Old UI and the employee form.
// Form.Item injects value/onChange; the value stays the selected day options
// so the save mapping in WorkingPatternTab is unchanged.
const ScheduleInput = ({
  value,
  onChange,
}: {
  value?: Option<string>[] | null;
  onChange?: (value: Option<string>[]) => void;
}) => {
  const selected = new Set((value ?? []).map((option) => option.value));
  return (
    <AppWorkingPatternSchedule
      days={Object.fromEntries(
        SCHEDULE_DAYS.map((day) => [day, selected.has(day.toUpperCase())]),
      )}
      onChange={(day, checked) => {
        const changed = day.toUpperCase();
        onChange?.(
          WORKING_PATTERN_OPTIONS.filter((option) =>
            option.value === changed ? checked : selected.has(option.value),
          ),
        );
      }}
    />
  );
};

const WorkingPatternForm: React.FC<WorkingPatternFormProps> = ({
  formRef,
  onSubmit,
  defaultValues = {},
}) => {
  const { messages } = useTypedIntl();

  const [form] = Form.useForm<WorkingPatternValues>();

  useImperativeHandle(formRef, () => ({
    submit() {
      form.submit();
    },
    reset() {
      form.resetFields();
    },
  }));

  const onFinish: FormProps['onFinish'] = async (values) => {
    await onSubmit?.(values);
  };

  const onFinishFailed: FormProps['onFinishFailed'] = (errorInfo) => {
    console.log('Failed:', errorInfo);
  };

  return (
    <AppFormContainer
      form={form}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      initialValues={defaultValues}
      layout="vertical"
      labelCol={{ span: 24 }}
      wrapperCol={{ span: 24 }}
    >
      {/* Name | Set as Default on one row, as in the Old UI. */}
      <Row gutter={24} align="bottom">
        <Col xs={24} sm={12}>
          <Form.Item
            label={
              messages['common.workingPatternName'] ?? 'Working Pattern Name'
            }
            name="name"
            rules={[{ required: true, message: messages['form.pleaseEnter'] }]}
          >
            <AppInput
              placeholder={
                messages['common.enterWorkingPatternName'] ??
                'Enter working pattern name'
              }
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            label={messages['common.setAsWorkingPatternDefault']}
            name="isDefault"
            rules={[{ required: true, message: messages['form.pleaseSelect'] }]}
          >
            <AppRadioGroup options={yesNoOptions} />
          </Form.Item>
        </Col>
      </Row>
      {/* FTE | Actual side by side, bottom-aligned so the inputs line up
          when the longer FTE label wraps (same as the employee form). */}
      <Row gutter={24} align="bottom">
        <Col xs={24} sm={12}>
          <Form.Item
            name="workingDaysFTE"
            label="Working Days (Full-Time Equivalent)"
            rules={[
              { required: true, message: messages['form.pleaseEnter'] },
              rangeRule('Working Days FTE', 7),
            ]}
          >
            <AppRateInput placeholder={messages['form.enter']} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name="workingDays"
            label="Working Days (Actual)"
            rules={[
              { required: true, message: messages['form.pleaseEnter'] },
              rangeRule('Working Days', 7),
            ]}
          >
            <AppRateInput placeholder={messages['form.enter']} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name="workingHoursFTE"
            label="Working Hours (Full-Time Equivalent)"
            rules={[
              { required: true, message: messages['form.pleaseEnter'] },
              rangeRule('Working Hours FTE', 168),
            ]}
          >
            <AppRateInput placeholder={messages['form.enter']} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name="workingHours"
            label="Working Hours (Actual)"
            rules={[
              { required: true, message: messages['form.pleaseEnter'] },
              rangeRule('Working Hours', 168),
            ]}
          >
            <AppRateInput placeholder={messages['form.enter']} />
          </Form.Item>
        </Col>
      </Row>
      <Form.Item
        name="workingPattern"
        label="Schedule per week"
        dependencies={['workingDays']}
        rules={[
          ({ getFieldValue }) => ({
            // `required` keeps the asterisk; the validator also runs on an
            // empty pick, which a plain required rule misses for a list.
            required: true,
            validator(_, value) {
              const selectedDaysCount = value?.length || 0;
              if (!selectedDaysCount) {
                return Promise.reject(
                  new Error('At least one day must be selected'),
                );
              }
              // Old UI parity: the schedule must match Working Days (Actual)
              // exactly — a fractional value never matches a day count.
              const workingDays = getFieldValue('workingDays');
              if (
                workingDays !== undefined &&
                workingDays !== null &&
                workingDays !== '' &&
                selectedDaysCount !== Number(workingDays)
              ) {
                return Promise.reject(
                  new Error('Schedule must match Working Days (Actual)'),
                );
              }
              return Promise.resolve();
            },
          }),
        ]}
      >
        <ScheduleInput />
      </Form.Item>
    </AppFormContainer>
  );
};

export default WorkingPatternForm;
